import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';

import { SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles/enums';
import {
  BehaviorSubject,
  combineLatest,
  Observable,
  of,
  Subscription,
  distinctUntilChanged,
  filter,
  map,
  startWith,
} from 'rxjs';

import {
  ADDRESS_PATTERN,
  CITY_PATTERN,
  isTruthy,
  LocalDate,
  NAME_PATTERN,
  rangeBetween,
  rangeFrom,
  unsubscribe,
} from '@fcom/core/utils';
import { numberOfDaysInMonth } from '@fcom/common-booking/utils/common-booking.utils';
import { ElementActions, ElementTypes, GaContext, SelectOption, ToasterMessage, ToasterService } from '@fcom/common';
import { LanguageService } from '@fcom/ui-translate';
import { ButtonSize, ButtonTheme, ButtonType, IconPosition, NotificationTheme } from '@fcom/ui-components';
import { GtmService } from '@fcom/common/gtm';
import {
  FinnairAdvancePassengerInformationRequest,
  FinnairAddressData,
  FinnairGender,
  FinnairOrder,
  FinnairPassengerItem,
  FinnairTravelDocumentType,
} from '@fcom/dapi/api/models';
import {
  formatDate,
  formatDateOfBirth,
  formatToDateOfBirth,
  formatToDateOfDateString,
  passportExpiryValidator,
} from '@fcom/common-booking/utils/date-utils';
import { finShare } from '@fcom/rx';
import { TypedFormGroup } from '@fcom/service-forms';

import { ZIP_CODE_PATTERN } from '../../../../../core/utils/utils';
import { ManageBookingService } from '../../../../services';
import {
  CANCEL_APIS_UPDATE,
  ERROR_APIS_UPDATE,
  FULL_APIS_UPDATE,
  PARTIAL_APIS_UPDATE,
  USER_OTHER_ADDRESS_APIS,
} from '../../../../constants';
import { generateApisToasterMessageIdByType } from '../../travel-preparations-utils';
import { travelRequirementsRequired } from '../../../../utils/booking.utils';

const generateMessageByType = (type: 'success' | 'error'): ToasterMessage => {
  return {
    id: generateApisToasterMessageIdByType(type),
    contentKey: `MMB.apis.${type}UpdatingApis`,
    theme: type === 'success' ? NotificationTheme.SUCCESS : NotificationTheme.ALERT,
  };
};

export const ApisPaxFormFields = {
  passengerInformation: 'passengerInformation',
  residencyAddress: 'residencyAddress',
  destinationAddress: 'destinationAddress',
  travelDocument: 'travelDocument',
};

interface ApisPaxForm {
  passengerInformation: {
    firstName: string;
    middleName: string;
    lastName: string;
    gender: FinnairGender;
    birthDate: {
      dateOfBirth: string;
      monthOfBirth: string;
      yearOfBirth: string;
    };
  };
  residencyAddress: {
    city: string;
    country: string;
  };
  destinationAddress: {
    address: string;
    destinationCity: string;
    state: string;
    zipCode: string;
    destinationCountry: string;
  };
  travelDocument: {
    documentType: FinnairTravelDocumentType;
    documentNumber: string;
    issuingCountry: string;
    nationality: string;
    validityDate: {
      day: string;
      month: string;
      year: string;
    };
  };
  confirm: boolean;
}

const getDays$ = (yearControl: AbstractControl, monthControl: AbstractControl) => {
  return combineLatest([
    yearControl.valueChanges.pipe(startWith(yearControl.value as string), filter(isTruthy)),
    monthControl.valueChanges.pipe(startWith(monthControl.value as string), filter(isTruthy)),
  ]).pipe(
    map(([year, month]: [number, number]) => numberOfDaysInMonth(Number(year))[month - 1]),
    map((numberOfDays) => rangeFrom(1, numberOfDays)),
    startWith(rangeFrom(1, 31)),
    distinctUntilChanged(),
    finShare()
  );
};

@Component({
  selector: 'fin-apis-pax-form',
  templateUrl: 'apis-pax-form.component.html',
  styleUrls: ['./apis-pax-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ApisPaxFormComponent implements OnInit, OnDestroy {
  readonly ButtonType = ButtonType;
  readonly ButtonSize = ButtonSize;
  readonly ButtonTheme = ButtonTheme;
  readonly IconPosition = IconPosition;
  readonly ApisPaxFormFields = ApisPaxFormFields;
  readonly SvgLibraryIcon = SvgLibraryIcon;

  @Input()
  labelledBy: string;

  @Input()
  passenger: FinnairPassengerItem;

  @Input()
  requiredForms: string[];

  @Input()
  addresses: Array<{ name: string; id: string; address: FinnairAddressData }>;

  @Output()
  closeForm: EventEmitter<void> = new EventEmitter<void>();
  @Output()
  formDirtyStatus: EventEmitter<boolean> = new EventEmitter();

  apisForm: TypedFormGroup<UntypedFormGroup>;
  addressGroup: UntypedFormGroup;
  addressOptions: SelectOption[];

  updatingApis$ = new BehaviorSubject(false);
  availableDays$: Observable<SelectOption[]>;
  availableMonths$: Observable<SelectOption[]> = of(
    rangeFrom(1, 12).map((month) => ({ name: String(month), value: String(month) }))
  );
  availableYears$: Observable<SelectOption[]> = of(
    rangeBetween(LocalDate.now().minusYears(100).localYear, LocalDate.now().localYear)
      .reverse()
      .map((year) => ({ name: String(year), value: String(year) }))
  );
  validityDays$: Observable<SelectOption[]>;
  validityYears$: Observable<SelectOption[]> = of(
    rangeBetween(LocalDate.now().localYear, LocalDate.now().plusYears(20).localYear).map((year) => ({
      name: String(year),
      value: String(year),
    }))
  );
  countryCodes$: Observable<SelectOption[]>;
  countryRegions$: Observable<SelectOption[]>;
  nationalities$: Observable<SelectOption[]>;
  genders$: Observable<SelectOption[]>;

  private subscriptions: Subscription = new Subscription();

  constructor(
    private fb: UntypedFormBuilder,
    private languageService: LanguageService,
    private mmbService: ManageBookingService,
    private toasterService: ToasterService,
    private gtmService: GtmService
  ) {}

  ngOnInit(): void {
    this.genders$ = this.languageService.translate('MMB.apis.form.genders').pipe(
      map((genders) => {
        return [FinnairGender.MALE, FinnairGender.FEMALE, FinnairGender.UNSPECIFIED].map((gender) => ({
          name: genders[gender.toLocaleLowerCase()] ?? gender,
          value: gender,
        }));
      })
    );

    this.countryRegions$ = this.languageService.translate('COUNTRY_REGIONS.us').pipe(
      map((regions) => {
        if (!regions) {
          return [];
        }

        return Object.keys(regions).map((region) => ({ value: region.toUpperCase(), name: regions[region] }));
      }),
      finShare()
    );

    const countries$ = this.languageService.translate('countryCodes').pipe(
      map((codes) => {
        if (!Array.isArray(codes)) {
          return [];
        }

        return codes;
      }),
      finShare()
    );

    this.countryCodes$ = countries$.pipe(
      map((codes) => codes.map((code) => ({ value: code.countryCode, name: code.name }))),
      finShare()
    );

    this.nationalities$ = countries$.pipe(
      map((codes) =>
        codes
          .map((code) => ({ value: code.countryCode, name: code.nationality ?? code.name }))
          .sort((a, b) => a.name.localeCompare(b.name))
      ),
      finShare()
    );

    const paxBirthDate = formatToDateOfBirth(
      this.passenger.advancePassengerInformation?.passengerInformation.data.dateOfBirth
    );
    const birthDate = this.fb.group(
      {
        dateOfBirth: [paxBirthDate?.dateOfBirth ?? '', Validators.required],
        monthOfBirth: [paxBirthDate?.monthOfBirth ?? '', Validators.required],
        yearOfBirth: [paxBirthDate?.yearOfBirth ?? '', Validators.required],
      },
      {
        validators: [],
      }
    );

    const getDateGroup = (oldDate = '') => {
      const date = formatToDateOfDateString(oldDate);
      return this.fb.group(
        {
          day: [date?.day ?? ''],
          month: [date?.month ?? ''],
          year: [date?.year ?? ''],
        },
        {
          validators: passportExpiryValidator(),
        }
      );
    };

    const paxInfo = this.passenger.advancePassengerInformation?.passengerInformation.data;
    const travelDocument = this.passenger.advancePassengerInformation?.travelDocument.data;
    const residencyAddress = this.passenger.advancePassengerInformation?.residencyAddress.data;
    const destinationAddress = this.passenger.advancePassengerInformation?.destinationAddress.data;

    const validityDate = getDateGroup(travelDocument?.documentValidityDate);

    const destinationAddressGroup = this.fb.group({
      address: [
        destinationAddress?.address ?? '',
        [Validators.minLength(1), Validators.maxLength(35), Validators.pattern(ADDRESS_PATTERN)],
      ],
      destinationCity: [destinationAddress?.city ?? '', [Validators.maxLength(35), Validators.pattern(CITY_PATTERN)]],
      state: [destinationAddress?.state ?? ''],
      zipCode: [
        destinationAddress?.zipCode ?? '',
        [Validators.minLength(1), Validators.maxLength(20), Validators.pattern(ZIP_CODE_PATTERN)],
      ],
      destinationCountry: [destinationAddress?.countryCode ?? ''],
    });

    this.apisForm = this.fb.group({
      ...(this.requiredForms.includes(ApisPaxFormFields.passengerInformation) && {
        [ApisPaxFormFields.passengerInformation]: this.fb.group({
          firstName: [
            paxInfo?.firstName ?? '',
            [Validators.required, Validators.minLength(2), Validators.maxLength(20), Validators.pattern(NAME_PATTERN)],
          ],
          middleName: [
            paxInfo?.middleName ?? '',
            [Validators.minLength(2), Validators.maxLength(20), Validators.pattern(NAME_PATTERN)],
          ],
          lastName: [
            paxInfo?.lastName ?? '',
            [Validators.required, Validators.minLength(2), Validators.maxLength(20), Validators.pattern(NAME_PATTERN)],
          ],
          gender: [paxInfo?.gender ?? '', Validators.required],
          birthDate,
        }),
      }),
      ...(this.requiredForms.includes(ApisPaxFormFields.travelDocument) && {
        [ApisPaxFormFields.travelDocument]: this.fb.group({
          documentType: [FinnairTravelDocumentType.PASSPORT],
          documentNumber: [travelDocument?.documentNumber ?? '', [Validators.maxLength(20)]],
          issuingCountry: [travelDocument?.documentIssuingCountry ?? ''],
          nationality: [travelDocument?.nationality ?? ''],
          validityDate,
        }),
      }),
      ...(this.requiredForms.includes(ApisPaxFormFields.residencyAddress) && {
        [ApisPaxFormFields.residencyAddress]: this.fb.group({
          city: [residencyAddress?.cityOfResidency ?? '', [Validators.maxLength(35), Validators.pattern(CITY_PATTERN)]],
          country: [residencyAddress?.countryOfResidency ?? ''],
        }),
      }),
      ...(this.requiredForms.includes(ApisPaxFormFields.destinationAddress) && {
        [ApisPaxFormFields.destinationAddress]: destinationAddressGroup,
      }),
      confirm: [false, Validators.requiredTrue],
    }) as TypedFormGroup<UntypedFormGroup>;

    this.addressGroup = this.fb.group({
      useSameAddress: [''],
    });

    this.subscriptions.add(
      this.addressGroup.get('useSameAddress').valueChanges.subscribe((selectedPassengerId: string) => {
        const selectedPassengerAddress = this.addresses.find((item) => item.id === selectedPassengerId)?.address;
        if (selectedPassengerAddress) {
          destinationAddressGroup.get('address').setValue(selectedPassengerAddress.address);
          destinationAddressGroup.get('destinationCity').setValue(selectedPassengerAddress.city);
          destinationAddressGroup.get('state').setValue(selectedPassengerAddress.state);
          destinationAddressGroup.get('zipCode').setValue(selectedPassengerAddress.zipCode);
          destinationAddressGroup.get('destinationCountry').setValue(selectedPassengerAddress.countryCode);
          destinationAddressGroup.markAllAsTouched();

          this.gtmService.trackElement(
            USER_OTHER_ADDRESS_APIS,
            GaContext.MANAGE_BOOKING,
            ElementTypes.LIST_ITEM,
            undefined,
            ElementActions.CLICK
          );
        }
      })
    );

    this.addressOptions = this.addresses
      .filter((item) => item.id !== this.passenger.id)
      .map((item) => ({
        name: item.name,
        value: item.id,
      }));

    this.availableDays$ = getDays$(birthDate.get('yearOfBirth'), birthDate.get('monthOfBirth')).pipe(
      map((days) => {
        return days.map((day) => {
          return { name: String(day), value: String(day) };
        });
      })
    );
    this.validityDays$ = getDays$(validityDate.get('year'), validityDate.get('month')).pipe(
      map((days) => {
        return days.map((day) => {
          return { name: String(day), value: String(day) };
        });
      })
    );
    this.subscriptions.add(
      this.apisForm.valueChanges.subscribe(() => {
        this.formDirtyStatus.emit(this.apisForm.dirty);
      })
    );
  }

  updateApis(): void {
    if (this.apisForm.disabled) {
      return;
    }

    if (!this.apisForm.valid) {
      this.apisForm.markAllAsTouched();
      this.apisForm.updateValueAndValidity();

      return;
    }

    this.apisForm.disable();
    this.updatingApis$.next(true);

    this.subscriptions.add(
      this.mmbService.updateApis([this.createPaxInformationRequestPayload()]).subscribe({
        next: (data: FinnairOrder) => {
          this.reportAndUpdateComponent('success', data);
        },
        error: () => {
          this.reportAndUpdateComponent('error');
        },
      })
    );
  }

  emitClose(): void {
    this.gtmService.trackElement(
      CANCEL_APIS_UPDATE,
      GaContext.MANAGE_BOOKING,
      ElementTypes.BUTTON,
      undefined,
      ElementActions.CLICK
    );

    this.closeForm.emit();
  }

  private reportAndUpdateComponent(type: 'success' | 'error', data?: FinnairOrder): void {
    this.apisForm.enable();
    this.updatingApis$.next(false);

    this.toasterService.addMessageToQueue(generateMessageByType(type));

    if (data && !travelRequirementsRequired(data, true)) {
      this.gtmService.trackElement(
        FULL_APIS_UPDATE,
        GaContext.MANAGE_BOOKING,
        ElementTypes.FORM,
        undefined,
        ElementActions.SUBMIT
      );
    } else {
      this.gtmService.trackElement(
        type === 'success' ? PARTIAL_APIS_UPDATE : ERROR_APIS_UPDATE,
        GaContext.MANAGE_BOOKING,
        ElementTypes.FORM,
        undefined,
        ElementActions.SUBMIT
      );
    }

    this.formDirtyStatus.emit(false);
    this.closeForm.emit();
  }

  private createPaxInformationRequestPayload(): FinnairAdvancePassengerInformationRequest {
    const paxForm: ApisPaxForm = this.apisForm.getRawValue();
    const validityDate = paxForm.travelDocument?.validityDate;

    return {
      travelerId: this.passenger.id,
      ...(this.requiredForms.includes(ApisPaxFormFields.passengerInformation) && {
        [ApisPaxFormFields.passengerInformation]: {
          firstName: paxForm.passengerInformation.firstName,
          middleName: paxForm.passengerInformation.middleName,
          lastName: paxForm.passengerInformation.lastName,
          gender: paxForm.passengerInformation.gender,
          dateOfBirth: formatDateOfBirth(paxForm.passengerInformation.birthDate),
        },
      }),
      ...(this.requiredForms.includes(ApisPaxFormFields.destinationAddress) && {
        [ApisPaxFormFields.destinationAddress]: {
          address: paxForm.destinationAddress.address,
          city: paxForm.destinationAddress.destinationCity,
          state: paxForm.destinationAddress.destinationCountry === 'US' ? paxForm.destinationAddress.state : '',
          zipCode: paxForm.destinationAddress.zipCode,
          countryCode: paxForm.destinationAddress.destinationCountry,
        },
      }),
      ...(this.requiredForms.includes(ApisPaxFormFields.residencyAddress) && {
        [ApisPaxFormFields.residencyAddress]: {
          cityOfResidency: paxForm.residencyAddress.city,
          countryOfResidency: paxForm.residencyAddress.country,
        },
      }),
      ...(this.requiredForms.includes(ApisPaxFormFields.travelDocument) && {
        [ApisPaxFormFields.travelDocument]: {
          documentType: paxForm.travelDocument.documentType,
          documentNumber: paxForm.travelDocument.documentNumber,
          documentIssuingCountry: paxForm.travelDocument.issuingCountry,
          nationality: paxForm.travelDocument.nationality,
          documentValidityDate: formatDate(validityDate.day, validityDate.month, validityDate.year),
        },
      }),
    };
  }

  ngOnDestroy(): void {
    this.subscriptions = unsubscribe(this.subscriptions);
  }
}
